import { FC, useEffect, useState } from 'react';
import CollapseFilter from '../../../components/CollapseFilter';
import { FileIcon } from '../../../icons';
import Features from '../Features/Features';
import { info } from '../mock';
import PriceBlock from '../PriceBlock/PriceBlock';
import {
  StyledBorder,
  StyledContainer,
  StyledDeliveryLogos,
  StyledFile,
  StyledFiles,
  StyledList,
  StyledPaymentsList,
} from './styles';
import { useAppSelector } from '../../../hooks/reduxHooks';

import fedexLogo from '../../../assets/images/img/fedex_logo.png';
import uspLogo from '../../../assets/images/img/ups-logo.png';
import mastercardLogo from '../../../assets/images/svg/mastercard.svg';
import visaLogo from '../../../assets/images/svg/visa.svg';
import { Typography } from '@mui/material';
import Image from 'next/image';
import { IProduct } from '../../../interfaces/product';

import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';

interface IProps {
  ssrProduct?: IProduct;
}

const ProductInfo: FC<IProps> = ({ ssrProduct }) => {
  const { documents } = info;
  const stateProduct = useAppSelector((st) => st.products.product);
  const locale = useAppSelector((st) => st.locale);

  const [product, setProduct] = useState<IProduct | undefined>(ssrProduct);

  useEffect(() => {
    if (stateProduct) setProduct(stateProduct);
  }, [stateProduct]);

  return (
    <StyledContainer>
      <PriceBlock ssrProduct={ssrProduct} />

      <div style={{ borderBottom: '1px solid #808080' }}>
        <CollapseFilter filterName={locale.product_shipping_and_delivery} forProduct={true} opened>
          <div style={{ paddingTop: 0, paddingBottom: '20px' }}>
            <StyledBorder></StyledBorder>
            <Typography
              variant={'body1'}
              sx={{
                fontSize: { md: '18px' },
                lineHeight: { md: '24px' },
                color: '#535353',
                maxWidth: { xs: '278px' },
              }}
            >
              {product?.inStock === 'chosen days'
                ? `Ready to be shipped in ${product?.deliveryTimes} days`
                : product?.inStock}
            </Typography>
            <p>{locale.product_delivery_help_text}</p>
            <StyledDeliveryLogos>
              <Image src={fedexLogo} alt='FedEx' title='FedEx' height='55' />
              <Image src={uspLogo} alt='UPS' title='UPS' height='55' />
            </StyledDeliveryLogos>
          </div>
        </CollapseFilter>
      </div>
      <div style={{ borderBottom: '1px solid #808080' }}>
        <CollapseFilter filterName={locale.product_payment_methods} forProduct={true}>
          <div style={{ paddingTop: 0, paddingBottom: '20px' }}>
            <StyledBorder></StyledBorder>
            <Typography
              variant={'body1'}
              component={'div'}
              sx={{
                fontSize: { md: '18px' },
                lineHeight: { md: '24px' },
                color: '#535353',
              }}
            >
              <p style={{ marginTop: 0 }}>{locale.product_terms_p3}</p>
              <StyledPaymentsList>
                <li>
                  <AccountBalanceWalletIcon />
                  {locale.product_terms_p3_1}
                </li>
                <li>
                  <AssuredWorkloadIcon />
                  {locale.product_terms_p3_2}
                </li>
                <li>
                  <CreditCardIcon />
                  {locale.product_terms_p3_3}
                </li>
                <li>
                  <CurrencyBitcoinIcon />
                  {locale.product_terms_p3_4}
                </li>
              </StyledPaymentsList>
              <p>{locale.product_payment_methods_p1}</p>

              <StyledDeliveryLogos>
                <Image src={mastercardLogo} alt='MasterCard' title='MasterCard' height='55' />
                <Image src={visaLogo} alt='Visa' title='Visa' height='55' style={{ scale: 1.5 }} />
              </StyledDeliveryLogos>
              <p>{locale.product_terms_p2}</p>
              <p>{locale.product_terms_p4}</p>
              <p>{locale.product_payment_methods_p2}</p>
            </Typography>
          </div>
        </CollapseFilter>
      </div>
      {product?.description && (
        <div style={{ borderBottom: '1px solid #808080' }}>
          <CollapseFilter filterName={locale['product_description'] ?? 'Description'} forProduct={true}>
            <div style={{ paddingTop: 0, paddingBottom: '20px' }}>
              <StyledBorder></StyledBorder>
              {product?.description && (
                <Typography
                  variant={'body1'}
                  sx={{ fontSize: { md: '18px' }, lineHeight: { md: '24px' }, color: '#535353' }}
                  component={'div'}
                >
                  <div dangerouslySetInnerHTML={{ __html: product?.description.replaceAll('\n', '<br/>') || '' }} />
                </Typography>
              )}
            </div>
          </CollapseFilter>
        </div>
      )}

      <div style={{ borderBottom: '1px solid #808080' }}>
        <CollapseFilter filterName={locale.product_terms_and_conditions} forProduct={true}>
          <div style={{ paddingTop: 0, paddingBottom: '20px' }}>
            <StyledBorder></StyledBorder>
            <Typography
              variant={'body1'}
              component={'div'}
              sx={{ fontSize: { md: '18px' }, lineHeight: { md: '24px' }, color: '#535353' }}
            >
              <p style={{ marginTop: '0' }}>{locale.product_terms_p1}</p>

              <div>
                {locale.product_terms_p5}
                <StyledList>
                  <li>{locale.product_terms_p5_1}</li>
                  <li>{locale.product_terms_p5_2}</li>
                  <li>{locale.product_terms_p5_3}</li>
                  <li>{locale.product_terms_p5_4}</li>
                  <li>{locale.product_terms_p5_5}</li>
                </StyledList>
              </div>
              <p>{locale.product_terms_p6}</p>
            </Typography>
          </div>
        </CollapseFilter>
      </div>

      <CollapseFilter filterName={locale['product_features'] ?? 'Features'} opened forProduct={true}>
        <Features ssrProduct={ssrProduct} />
      </CollapseFilter>
      {product?.documents?.length ? (
        <CollapseFilter filterName={documents.title} forProduct={true}>
          <StyledFiles>
            {product?.documents?.map((item, index) => (
              <StyledFile key={index} href={item} target='_blank' rel='noreferrer'>
                <FileIcon size={48} />
              </StyledFile>
            ))}
          </StyledFiles>
        </CollapseFilter>
      ) : null}
    </StyledContainer>
  );
};

export default ProductInfo;
